/**
 * AppealSection - Output section for appeal information
 *
 * Displays appeal status, deadline, and submission methods.
 */

import { AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import React from 'react';

import { FieldPathProvider } from '../context';
import { Field, SectionTriggerScore } from './Field';

interface AppealSectionProps {
  data?: Record<string, unknown>;
  sectionId: string;
  entityType?: 'drug' | 'procedure';
}

export const AppealSection: React.FC<AppealSectionProps> = ({
  data,
  sectionId,
  entityType = 'drug',
}) => {
  const label = entityType === 'drug' ? 'Drug' : 'Procedure';

  return (
    <AccordionItem value={sectionId} className="border-b">
      <AccordionTrigger className="px-4 py-3 text-sm font-medium hover:bg-muted/50">
        <div className="flex items-center gap-2 flex-1">
          <span>{label} Appeal</span>
          <SectionTriggerScore subsectionKey="appeal" />
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4">
        <FieldPathProvider prefix={`${entityType}.appeal`}>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <Field
              label="Appeal Available"
              value={data?.prior_authorization_appeal_available}
              fieldId="prior_authorization_appeal_available"
            />
            <Field
              label="Department Name"
              value={data?.prior_authorization_appeal_department_name}
              fieldId="prior_authorization_appeal_department_name"
            />
            <Field
              label="Fax Number"
              value={data?.prior_authorization_appeal_fax_number}
              fieldId="prior_authorization_appeal_fax_number"
            />
            <Field
              label="Phone Number"
              value={data?.prior_authorization_appeal_phone_number}
              fieldId="prior_authorization_appeal_phone_number"
            />
            <Field
              label="Website"
              value={data?.prior_authorization_appeal_website}
              fieldId="prior_authorization_appeal_website"
            />
            <Field
              label="Turnaround Time"
              value={data?.prior_authorization_appeal_turnaround_time}
              fieldId="prior_authorization_appeal_turnaround_time"
            />
            <Field
              label="Times Allowed"
              value={data?.prior_authorization_appeal_number_of_times_allowed}
              fieldId="prior_authorization_appeal_number_of_times_allowed"
            />
            <Field
              label="Requirements"
              value={data?.prior_authorization_appeal_requirements}
              fieldId="prior_authorization_appeal_requirements"
            />
          </div>
        </FieldPathProvider>
      </AccordionContent>
    </AccordionItem>
  );
};
